import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Divider, Menu } from 'antd';
import styles from './style.module.scss';
import { FeatureFlag } from '../../../../utils/feature-flags/FeatureFlagsProvider';
import Guard from '../../../../pages/permissions/crew-schedule-v2-guard';
import { getCommon, getMenu } from '../../../../redux/selectors';
import Icon from '@ant-design/icons';
import { Scrollbars } from 'react-custom-scrollbars';

interface MobileDropdownMenuProps {
  closeOnSelect: () => void;
}

const ITEM_HEIGHT = 60;
const MAX_CONTAINER_HEIGHT = window.innerHeight * 0.7;

const MobileDropdownMenu: React.FC<MobileDropdownMenuProps> = ({ closeOnSelect }) => {
  const { menuLeftData: menuData } = useSelector(getMenu);
  const guard = new Guard('pages');
  const { featureFlags } = useSelector(getCommon);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    if (menuData.some(menuItem => menuItem.key === latestOpenKey)) {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    } else {
      setOpenKeys(keys);
    }
  };

  const generateItem = (item: any) => {
    const { key, title, url, disabled } = item;
    const icon = item ? getIcon(item) : null;

    if (item.divider) {
      return <Divider key={Math.random()} />;
    }
    return (
      <Menu.Item
        key={key}
        disabled={disabled}
        className={styles.menuItem}
        style={{ height: '60px', lineHeight: '60px' }}
      >
        {item.target ? (
          <a onClick={closeOnSelect} href={url} target={item.target} rel="noopener noreferrer">
            <span className={styles.title}>{title}</span>
          </a>
        ) : (
          <Link onClick={closeOnSelect} to={url} className={styles.menuChildren}>
            {icon}
            <span className={styles.title}>{title}</span>
          </Link>
        )}
      </Menu.Item>
    );
  };
  const getIcon = menuItem => {
    if (!menuItem) return null;
    const { iconName, iconComponent } = menuItem;
    if (iconComponent) {
      return <Icon component={iconComponent} className={`${styles.icon} icon-collapsed-hidden`} />;
    }
    if (iconName) {
      return (
        <Icon
          style={{
            marginRight: '8px',
            lineHeight: 'inherit',
            float: 'right',
            color: 'white',
          }}
          type={iconName}
        />
      );
    }
    return null;
  };

  const generateMenuItems = (items: any[]) => {
    return items
      .filter(menuItem => {
        if (
          menuItem.key === 'crew-scheduling-mobile' &&
          !featureFlags[FeatureFlag.CrewScheduleMobile]
        ) {
          return false;
        }
        if (menuItem.key === 'flightWatch' && !featureFlags[FeatureFlag.FlightWatch]) {
          return false;
        }
        return guard.canAccess(`${menuItem.key}`);
      })
      .map(menuItem => {
        if (menuItem.children) {
          return menuItem.children.map(child => generateItem(child));
        }
        return generateItem(menuItem);
      })
      .flat();
  };

  const menuItems = useMemo(() => {
    return menuData.flatMap(menuItem =>
      menuItem.children
        ? menuItem.children.map(child => generateItem(child))
        : generateItem(menuItem),
    );
  }, [menuData]);

  const calculatedHeight = menuItems?.length * ITEM_HEIGHT;
  const containerHeight = Math.min(calculatedHeight, MAX_CONTAINER_HEIGHT);

  return (
    <div style={{ backgroundColor: '#001529', height: containerHeight }}>
      <Scrollbars
        className={styles.scrollbarMobile}
        renderThumbVertical={({ style, ...styleProps }) => (
          <div
            {...styleProps}
            style={{
              ...style,
              width: '4px',
              borderRadius: 'inherit',
              backgroundColor: '#c5cdd2',
              left: '1px',
            }}
          />
        )}
        autoHide
      >
        <Menu
          theme={'dark'}
          mode="inline"
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          style={{ background: 'rgba(0, 0, 0, .85)' }}
        >
          {menuData.flatMap(menuItem => generateMenuItems(menuItem.children ?? [menuItem]))}
        </Menu>
      </Scrollbars>
    </div>
  );
};

export default MobileDropdownMenu;
